import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';

export const useGetLiabilityAndEquityTableHeadings = (isExamplePage: boolean) => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const currentLiabilitiesVariants = [
      isExamplePage
        ? 'Accounts payable'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.accountsPayable',
          ),
      isExamplePage
        ? 'Short-term Debt'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.shortTermDebt',
          ),
      isExamplePage
        ? 'Current Portion of Long-term Debt'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.currentPortionOfLongTermDebt',
          ),
      isExamplePage
        ? 'Accrued liabilities'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.accruedLiabilities',
          ),
      isExamplePage
        ? 'Deferred Revenue'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.deferredRevenue',
          ),
      isExamplePage
        ? 'Deferred tax liabilities'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.deferredTaxLiabilities',
          ),
      isExamplePage
        ? 'Provisions'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.provisions',
          ),
      isExamplePage
        ? 'Other current liabilities'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.otherCurrentLiabilities',
          ),
    ];
    const nonCurrentLiabilitiesVariants = [
      isExamplePage
        ? 'Long-term Debt'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.longTermDebt',
          ),
      isExamplePage
        ? 'Deferred Tax Liabilities'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.deferredTaxLiabilities',
          ),
      isExamplePage
        ? 'Pension Liabilities'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.pensionLiabilities',
          ),
      isExamplePage
        ? 'Provisions'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.provisionsNonCurrent',
          ),
      isExamplePage
        ? 'Other non-current liabilities'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.otherNonCurrentLiabilities',
          ),
    ];
    const equityVariants = [
      isExamplePage
        ? "Owners' Equity"
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.ownersEquity'),
      isExamplePage
        ? 'Additional paid-in capital'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.additionalPaidInCapital'),
      isExamplePage
        ? 'Reserves'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.reserves'),
      isExamplePage
        ? 'Accumulated other comprehensive Income'
        : t(
            'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.accumulatedOtherComprehensiveIncome',
          ),
      isExamplePage
        ? 'Retained earnings'
        : t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.retainedEarnings'),
    ];
    const currentLiabilitiesTotalRowTitle = t(
      'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.totalRowTitle',
    );
    const nonCurrentLiabilitiesTotalRowTitle = t(
      'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.totalRowTitle',
    );
    const equityTotalRowTitle = t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.totalRowTitle');
    const totalLiabilitiesAndEquity = t(
      'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.totalLiabilitiesAndEquity',
    );
    return {
      currentLiabilitiesVariants,
      nonCurrentLiabilitiesVariants,
      equityVariants,
      currentLiabilitiesTotalRowTitle,
      nonCurrentLiabilitiesTotalRowTitle,
      equityTotalRowTitle,
      totalLiabilitiesAndEquity,
    };
  }, [t, i18n, isExamplePage]);
};

export const currentLiabilitiesVariants = [
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.accountsPayable',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.shortTermDebt',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.currentPortionOfLongTermDebt',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.accruedLiabilities',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.deferredRevenue',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.deferredTaxLiabilities',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.provisions',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.currentLiabilities.currentLiabilitiesRowTitles.otherCurrentLiabilities',
  ),
];

export const nonCurrentLiabilitiesVariants = [
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.longTermDebt',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.deferredTaxLiabilities',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.pensionLiabilities',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.provisionsNonCurrent',
  ),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.nonCurrentLiabilities.nonCurrentLiabilitiesRowTitles.otherNonCurrentLiabilities',
  ),
];

export const equityVariants = [
  t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.ownersEquity'),
  t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.additionalPaidInCapital'),
  t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.reserves'),
  t(
    'financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.accumulatedOtherComprehensiveIncome',
  ),
  t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.equity.equityRowTitles.retainedEarnings'),
];

export const LIABILITIES_AND_EQUITY_FORM_NAMES = {
  CURRENT_LIABILITIES_TABLE: 'currentLiabilitiesTable',
  CURRENT_LIABILITIES_TABLE_TOTAL: 'currentLiabilitiesTableTotal',
  NON_CURRENT_LIABILITIES_TABLE: 'nonCurrentLiabilitiesTable',
  NON_CURRENT_LIABILITIES_TABLE_TOTAL: 'nonCurrentLiabilitiesTableTotal',
  EQUITY_TABLE: 'equityTable',
  EQUITY_TABLE_TOTAL: 'equityTableTotal',
  TOTAL_LIABILITIES_AND_EQUITY: 'totalLiabilitiesAndEquity',
} as const;

export const CURRENT_LIABILITIES_TABLE_NAMES = {
  ACCOUNTS_PAYABLE: 'accountsPayable',
  SHORT_TERM_DEBT: 'shortTermDebt',
  CURRENT_PORTION_OF_LONG_TERM_DEBT: 'currentPortionOfLongTermDebt',
  ACCRUED_LIABILITIES: 'accruedLiabilities',
  DEFERRED_REVENUE: 'deferredRevenue',
  DEFERRED_TAX_LIABILITIES: 'deferredTaxLiabilities',
  PROVISIONS: 'provisions',
  OTHER_CURRENT_LIABILITIES: 'otherCurrentLiabilities',
} as const;

export const NON_CURRENT_LIABILITIES_TABLE_NAMES = {
  LONG_TERM_DEBT: 'longTermDebt',
  DEFERRED_TAX_LIABILITIES: 'deferredTaxLiabilities',
  PENSION_LIABILITIES: 'pensionLiabilities',
  PROVISIONS_NON_CURRENT: 'provisionsNonCurrent',
  OTHER_NON_CURRENT_LIABILITIES: 'otherNonCurrentLiabilities',
} as const;

export const EQUITY_TABLE_NAMES = {
  OWNERS_EQUITY: 'ownersEquity',
  ADDITIONAL_PAID_IN_CAPITAL: 'additionalPaidInCapital',
  RESERVES: 'reserves',
  ACCUMULATED_OTHER_COMPREHENSIVE_INCOME: 'accumulatedOtherComprehensiveIncome',
  RETAINED_EARNINGS: 'retainedEarnings',
} as const;

export interface ILiabilitiesFormValuesType {
  [LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE]: {
    [CURRENT_LIABILITIES_TABLE_NAMES.ACCOUNTS_PAYABLE]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.SHORT_TERM_DEBT]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.CURRENT_PORTION_OF_LONG_TERM_DEBT]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.ACCRUED_LIABILITIES]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_REVENUE]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_TAX_LIABILITIES]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.PROVISIONS]: IPeriodsTableRow;
    [CURRENT_LIABILITIES_TABLE_NAMES.OTHER_CURRENT_LIABILITIES]: IPeriodsTableRow;
  };
  [LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE]: {
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.LONG_TERM_DEBT]: IPeriodsTableRow;
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_TAX_LIABILITIES]: IPeriodsTableRow;
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.PENSION_LIABILITIES]: IPeriodsTableRow;
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.PROVISIONS_NON_CURRENT]: IPeriodsTableRow;
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.OTHER_NON_CURRENT_LIABILITIES]: IPeriodsTableRow;
  };
  [LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE]: {
    [EQUITY_TABLE_NAMES.OWNERS_EQUITY]: IPeriodsTableRow;
    [EQUITY_TABLE_NAMES.ADDITIONAL_PAID_IN_CAPITAL]: IPeriodsTableRow;
    [EQUITY_TABLE_NAMES.RESERVES]: IPeriodsTableRow;
    [EQUITY_TABLE_NAMES.ACCUMULATED_OTHER_COMPREHENSIVE_INCOME]: IPeriodsTableRow;
    [EQUITY_TABLE_NAMES.RETAINED_EARNINGS]: IPeriodsTableRow;
  };
  [LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE_TOTAL]: IPeriodsTableRow;
  [LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE_TOTAL]: IPeriodsTableRow;
  [LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE_TOTAL]: IPeriodsTableRow;
  [LIABILITIES_AND_EQUITY_FORM_NAMES.TOTAL_LIABILITIES_AND_EQUITY]: IPeriodsTableRow;
}

export const liabilitiesAndEquityInitialValues: ILiabilitiesFormValuesType = {
  [LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE]: {
    [CURRENT_LIABILITIES_TABLE_NAMES.ACCOUNTS_PAYABLE]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.SHORT_TERM_DEBT]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.CURRENT_PORTION_OF_LONG_TERM_DEBT]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.ACCRUED_LIABILITIES]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_REVENUE]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_TAX_LIABILITIES]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.PROVISIONS]: initialPeriodsTableRow,
    [CURRENT_LIABILITIES_TABLE_NAMES.OTHER_CURRENT_LIABILITIES]: initialPeriodsTableRow,
  },
  [LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE]: {
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.LONG_TERM_DEBT]: initialPeriodsTableRow,
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.DEFERRED_TAX_LIABILITIES]: initialPeriodsTableRow,
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.PENSION_LIABILITIES]: initialPeriodsTableRow,
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.PROVISIONS_NON_CURRENT]: initialPeriodsTableRow,
    [NON_CURRENT_LIABILITIES_TABLE_NAMES.OTHER_NON_CURRENT_LIABILITIES]: initialPeriodsTableRow,
  },
  [LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE]: {
    [EQUITY_TABLE_NAMES.OWNERS_EQUITY]: initialPeriodsTableRow,
    [EQUITY_TABLE_NAMES.ADDITIONAL_PAID_IN_CAPITAL]: initialPeriodsTableRow,
    [EQUITY_TABLE_NAMES.RESERVES]: initialPeriodsTableRow,
    [EQUITY_TABLE_NAMES.ACCUMULATED_OTHER_COMPREHENSIVE_INCOME]: initialPeriodsTableRow,
    [EQUITY_TABLE_NAMES.RETAINED_EARNINGS]: initialPeriodsTableRow,
  },
  [LIABILITIES_AND_EQUITY_FORM_NAMES.CURRENT_LIABILITIES_TABLE_TOTAL]: initialPeriodsTableRow,
  [LIABILITIES_AND_EQUITY_FORM_NAMES.NON_CURRENT_LIABILITIES_TABLE_TOTAL]: initialPeriodsTableRow,
  [LIABILITIES_AND_EQUITY_FORM_NAMES.EQUITY_TABLE_TOTAL]: initialPeriodsTableRow,
  [LIABILITIES_AND_EQUITY_FORM_NAMES.TOTAL_LIABILITIES_AND_EQUITY]: initialPeriodsTableRow,
};
